import React, { useId, useRef, useState } from 'react';
import { Paperclip, X } from 'lucide-react';
import { Button } from './Button';
import { FileTile } from './FileChip';

// "the kick" from "Kick", but "the TR-808" stays as it is
export const inSentence = (name) => (/^[A-Z][a-z]/.test(name) ? name[0].toLowerCase() + name.slice(1) : name);

/**
 * The message bar at the bottom of a clip's chat: ask a follow-up, or attach the clip's next version.
 * The attached file is the parent's state (so a file dropped anywhere in the chat can set it);
 * onSubmit({ text, file }) is called on send. The text clears straight away and comes back if
 * onSubmit throws or returns false. While a version is being analyzed, `lockedPlaceholder` locks it
 * and says when it opens again.
 */
export const Composer = ({
  clipName,
  nextVersion,
  file = null,
  onFileChange,
  onSubmit,
  busy = false,
  lockedPlaceholder,
  accept = 'audio/*',
  maxLength,
}) => {
  const [text, setText] = useState('');
  const fileInputRef = useRef(null);
  const inputId = useId();
  const locked = Boolean(lockedPlaceholder);
  const canSend = !busy && !locked && Boolean(file || text.trim());

  const submit = async (event) => {
    event.preventDefault();
    if (!canSend) return;
    const sent = text;
    setText(''); // the thread shows it straight away
    // If it didn't go through, put back what they typed (unless they've started typing again)
    const restore = () => setText((current) => current || sent);
    try {
      if ((await onSubmit({ text: sent.trim(), file })) === false) restore();
    } catch {
      restore(); // the parent shows the error
    }
  };

  return (
    <form onSubmit={submit} className="w-full max-w-chat-max mx-auto flex flex-col gap-space-2 font-text">
      {file && (
        <div className="flex items-center gap-space-3 px-space-3 py-space-2 rounded-radius-md bg-surface border border-line">
          <FileTile />
          <span className="flex-1 min-w-0 flex flex-col gap-0.5">
            <span className="truncate font-mono text-data-sm text-ink">{file.name}</span>
            <span className="text-small text-ink-soft">Will be analyzed as V{nextVersion} of {clipName}</span>
          </span>
          <Button variant="icon" icon={X} aria-label="Remove attached file" onClick={() => onFileChange(null)} />
        </div>
      )}
      <div className={`flex items-center gap-space-2 p-space-2 rounded-radius-lg bg-surface border border-line has-[input:focus]:outline has-[input:focus]:outline-2 has-[input:focus]:outline-offset-2 has-[input:focus]:outline-focus${locked ? ' opacity-60' : ''}`}>
        <Button variant="icon" icon={Paperclip} aria-label="Attach a new version" onClick={() => fileInputRef.current?.click()} disabled={locked} />
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          className="hidden"
          tabIndex={-1}
          aria-hidden="true"
          data-testid="composer-file-input"
          onChange={(event) => {
            const picked = event.target.files?.[0];
            if (picked) onFileChange(picked);
            event.target.value = ''; // so picking the same file again still fires
          }}
        />
        <label htmlFor={inputId} className="sr-only">Ask about this clip</label>
        <input
          id={inputId}
          type="text"
          value={text}
          maxLength={maxLength}
          onChange={(event) => setText(event.target.value)}
          disabled={locked}
          placeholder={lockedPlaceholder || `Ask about the ${inSentence(clipName)}, or drop in V${nextVersion}…`}
          className="flex-1 min-w-0 min-h-target-min bg-transparent border-0 text-body text-ink placeholder:text-muted focus:outline-none"
        />
        <Button type="submit" disabled={!canSend}>{file ? `Analyze V${nextVersion}` : 'Send'}</Button>
      </div>
      {!locked && (
        <span className="text-caption text-muted">
          Drop a file anywhere in this chat to add it as V{nextVersion}. New instruments go in Add clip.
        </span>
      )}
    </form>
  );
};
